/**
 * LandingPage — the public marketing site.
 *
 * Three concerns kept in mind when rebuilding this page:
 *   1. Logged-in state: the header pivots to an avatar+name chip + "Abrir
 *      aplicación" CTA, so returning users don't see a login prompt they
 *      no longer need.
 *   2. Visible CTAs: every primary button now has a filled accent
 *      background and a soft shadow so the eye locks on it from anywhere
 *      on the page.
 *   3. Pricing teaser: a compact strip with the launch prices lives just
 *      above the footer so visitors don't have to dig into /planes to
 *      learn what the product costs.
 */

import { Link } from 'react-router-dom';
import {
  ArrowRight,
  Box,
  FileBarChart,
  Gauge,
  ShieldCheck,
  BookOpen,
  Cpu,
  Infinity as InfinityIcon,
  Sparkles,
  ChevronRight,
} from 'lucide-react';
import { Brand } from '@/components/Brand';
import { Button } from '@/components/ui/Button';
import { FierroIcon } from '@/components/icons/FierroIcon';
import { SiteFooter } from '@/components/layout/SiteFooter';
import { CartButton } from '@/features/billing/CartButton';
import { CartDrawer } from '@/features/billing/CartDrawer';
import { ThemeSwitcher } from '@/features/theme/ThemeSwitcher';
import { FaqList, FAQ_ITEMS } from '@/pages/legal/FaqPage';
import { Link as RouterLink } from 'react-router-dom';
import { useAuthStore } from '@/stores/authStore';

export function LandingPage() {
  return (
    <div className="min-h-screen bg-[var(--surface-base)] text-[var(--text-primary)]">
      <Header />
      <main>
        <Hero />
        <FeatureGrid />
        <PricingTeaser />
        <FaqSection />
        <NormsStrip />
      </main>
      <SiteFooter />
      {/* Global cart drawer — opens from the CartButton anywhere on the page. */}
      <CartDrawer />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Header — branching on auth state
// ---------------------------------------------------------------------------

function Header() {
  const user = useAuthStore((s) => s.user);
  const isLoggedIn = !!useAuthStore((s) => s.token);

  return (
    <header
      className="sticky top-0 z-30 backdrop-blur-md"
      style={{
        background: 'rgba(2, 6, 23, 0.7)',
        borderBottom: '1px solid var(--border-subtle)',
      }}
    >
      <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-6">
        <Link to="/" className="no-underline">
          <Brand size="md" />
        </Link>
        <nav className="hidden items-center gap-6 md:flex">
          <a href="#features" className="text-sm" style={{ color: 'var(--text-secondary)' }}>
            Módulos
          </a>
          <a href="#normas" className="text-sm" style={{ color: 'var(--text-secondary)' }}>
            Normas
          </a>
          <Link to="/planes" className="text-sm no-underline" style={{ color: 'var(--text-secondary)' }}>
            Planes
          </Link>
        </nav>
        <div className="flex items-center gap-2">
          <ThemeSwitcher />
          <CartButton />
          {isLoggedIn && user ? <UserChip /> : <GuestActions />}
        </div>
      </div>
    </header>
  );
}

/** Compact avatar+name pill linking to the dashboard. Replaces the
 *  "Iniciar sesión / Abrir aplicación" pair when the user is logged in. */
function UserChip() {
  const user = useAuthStore((s) => s.user);
  if (!user) return null;
  const initial = (user.name || user.email).slice(0, 1).toUpperCase();
  return (
    <Link to="/app/dashboard" className="no-underline">
      <button
        type="button"
        className="group flex h-10 items-center gap-2 rounded-[var(--radius-pill)] border px-3 text-sm transition-all hover:scale-[1.02]"
        style={{
          background: 'var(--surface-1)',
          borderColor: 'var(--border-default)',
          color: 'var(--text-primary)',
          boxShadow: '0 4px 14px rgba(245,158,11,0.10)',
        }}
        title={`${user.email} — entrar a la app`}
      >
        <span
          className="flex h-7 w-7 items-center justify-center rounded-full font-bold"
          style={{ background: 'var(--accent)', color: 'var(--surface-base)' }}
        >
          {initial}
        </span>
        <span className="hidden font-mono tabular text-[12px] sm:inline">
          {(user.name || user.email).slice(0, 20)}
        </span>
        <ChevronRight
          size={14}
          strokeWidth={2}
          style={{ color: 'var(--accent)' }}
          className="transition-transform group-hover:translate-x-0.5"
        />
      </button>
    </Link>
  );
}

function GuestActions() {
  return (
    <>
      <Link to="/login" className="no-underline">
        <Button variant="ghost" size="sm">
          Iniciar sesión
        </Button>
      </Link>
      <Link to="/login?tab=register" className="no-underline">
        <button
          type="button"
          className="inline-flex h-9 items-center gap-1.5 rounded-[var(--radius)] px-4 text-sm font-semibold transition-all hover:scale-[1.02]"
          style={{
            background: 'var(--accent)',
            color: 'var(--surface-base)',
            boxShadow: '0 6px 20px rgba(245,158,11,0.35)',
          }}
        >
          Crear cuenta
          <ArrowRight size={14} strokeWidth={2.4} />
        </button>
      </Link>
    </>
  );
}

// ---------------------------------------------------------------------------
// Hero
// ---------------------------------------------------------------------------

function Hero() {
  const isLoggedIn = !!useAuthStore((s) => s.token);

  return (
    <section className="relative overflow-hidden">
      <div
        className="pointer-events-none absolute inset-0 opacity-[0.07]"
        style={{
          backgroundImage:
            'linear-gradient(var(--text-muted) 1px, transparent 1px), linear-gradient(to right, var(--text-muted) 1px, transparent 1px)',
          backgroundSize: '40px 40px',
          maskImage: 'radial-gradient(ellipse at center, black 40%, transparent 80%)',
        }}
      />
      {/* Soft orange glow behind the title to anchor the eye. */}
      <div
        className="pointer-events-none absolute left-1/2 top-1/3 h-[420px] w-[680px] -translate-x-1/2 -translate-y-1/2 opacity-30 blur-3xl"
        style={{
          background:
            'radial-gradient(ellipse at center, rgba(245,158,11,0.32), transparent 60%)',
        }}
      />
      <div className="mx-auto max-w-7xl px-6 pb-24 pt-20 md:pb-32 md:pt-28">
        <div className="mx-auto max-w-3xl text-center">
          <div
            className="mb-6 inline-flex items-center gap-2 rounded-[var(--radius-pill)] border px-3 py-1 text-xs"
            style={{
              borderColor: 'var(--border-default)',
              background: 'var(--surface-1)',
              color: 'var(--text-secondary)',
            }}
          >
            <Sparkles size={12} strokeWidth={2} style={{ color: 'var(--accent)' }} />
            Precios de lanzamiento — primeros 6 meses
          </div>

          <h1
            className="mb-5 text-4xl md:text-6xl"
            style={{
              fontFamily: 'var(--font-display)',
              fontWeight: 800,
              letterSpacing: '-0.035em',
              lineHeight: 1.05,
            }}
          >
            Cálculo de concreto y acero
            <br />
            <span style={{ color: 'var(--accent)' }}>preciso, en vivo, profesional.</span>
          </h1>

          <p
            className="mx-auto mb-10 max-w-2xl text-base md:text-lg"
            style={{ color: 'var(--text-secondary)', lineHeight: 1.6 }}
          >
            Diseña losas, vigas, columnas, muros y zapatas con normativa peruana y americana.
            Diagramas P-M interactivos en 3D, armado paramétrico, verificaciones automáticas. Parte
            del ecosistema Alpha+.
          </p>

          <div className="flex flex-col items-center justify-center gap-3 sm:flex-row">
            <Link
              to={isLoggedIn ? '/app/dashboard' : '/login?tab=register'}
              className="no-underline"
            >
              <button
                type="button"
                className="inline-flex h-12 items-center gap-2 rounded-[var(--radius)] px-6 text-base font-semibold transition-all hover:scale-[1.02]"
                style={{
                  background: 'var(--accent)',
                  color: 'var(--surface-base)',
                  boxShadow: '0 10px 30px rgba(245,158,11,0.42)',
                }}
              >
                {isLoggedIn ? 'Abrir aplicación' : 'Crear cuenta gratis'}
                <ArrowRight size={18} strokeWidth={2.4} />
              </button>
            </Link>
            <Link to="/planes" className="no-underline">
              <button
                type="button"
                className="inline-flex h-12 items-center gap-2 rounded-[var(--radius)] border px-6 text-base font-medium transition-colors hover:bg-[var(--surface-2)]"
                style={{
                  borderColor: 'var(--border-strong)',
                  color: 'var(--text-primary)',
                  background: 'var(--surface-1)',
                }}
              >
                Ver planes
              </button>
            </Link>
          </div>

          <div className="mt-10 flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-xs" style={{ color: 'var(--text-muted)' }}>
            <span className="inline-flex items-center gap-1.5">
              <FierroIcon size={12} /> 15 fierros gratis al registrarte
            </span>
            <span aria-hidden style={{ color: 'var(--border-default)' }}>•</span>
            <span className="inline-flex items-center gap-1.5">
              <FierroIcon size={12} /> +15 fierros gratis cada mes
            </span>
            <span aria-hidden style={{ color: 'var(--border-default)' }}>•</span>
            <span className="inline-flex items-center gap-1.5">
              <InfinityIcon size={12} strokeWidth={2} style={{ color: 'var(--accent)' }} /> Suscripción desde S/. 39/mes
            </span>
          </div>
        </div>
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Feature grid (kept structurally similar, but module cards available now)
// ---------------------------------------------------------------------------

function FeatureGrid() {
  return (
    <section id="features" className="border-t" style={{ borderColor: 'var(--border-subtle)' }}>
      <div className="mx-auto max-w-7xl px-6 py-20">
        <div className="mb-12 max-w-2xl">
          <h2 className="mb-3 text-3xl" style={{ fontFamily: 'var(--font-display)', fontWeight: 700, letterSpacing: '-0.02em' }}>
            Cada elemento, su software.
          </h2>
          <p style={{ color: 'var(--text-secondary)' }}>
            Módulos especializados por elemento estructural. Inputs en vivo, visualización 2D/3D,
            exportación a PDF y Excel con detalle de cálculo.
          </p>
        </div>

        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
          <FeatureCard
            icon={Box}
            title="Diseño de columnas E.060"
            desc="Secciones rectangulares, L, T, cruciforme, circular. Diagrama P-M22/P-M33 interactivo en 3D. Cálculo en vivo de As, ρ, #varillas, ratio D/C."
            available
          />
          <FeatureCard icon={FileBarChart} title="Diseño de vigas" desc="Flexión, corte, control de fisuración, anclaje. Vigas peraltadas y chatas." available />
          <FeatureCard icon={Gauge} title="Diseño de losas" desc="Aligerada, maciza, una y dos direcciones. Cargas E.020, refuerzo positivo y negativo." available />
          <FeatureCard icon={Cpu} title="Predimensionamiento" desc="Wizard tipo Harmsen / Blanco Blasco para arrancar tu proyecto rápido." available />
          <FeatureCard icon={ShieldCheck} title="Auditoría E.060 / ACI" desc="Verificaciones automáticas con referencias a artículos de norma. Sin sorpresas en obra." available />
          <FeatureCard icon={BookOpen} title="Conectado a alphabim" desc="Tu cálculo viaja al modelo BIM, al metrado, al presupuesto. Sin re-tipear." />
        </div>
      </div>
    </section>
  );
}

function FeatureCard({
  icon: Icon,
  title,
  desc,
  available,
}: {
  icon: typeof Box;
  title: string;
  desc: string;
  available?: boolean;
}) {
  return (
    <div
      className="group relative rounded-[var(--radius-md)] border p-6 transition-all duration-200 hover:-translate-y-0.5 hover:bg-[var(--surface-2)]"
      style={{
        background: 'var(--surface-1)',
        borderColor: 'var(--border-default)',
      }}
    >
      <div
        className="mb-4 inline-flex h-10 w-10 items-center justify-center rounded-[var(--radius)]"
        style={{
          background: available ? 'var(--accent)' : 'var(--surface-3)',
          color: available ? 'var(--surface-base)' : 'var(--text-muted)',
        }}
      >
        <Icon size={20} strokeWidth={1.8} />
      </div>
      <h3
        className="mb-2 text-base"
        style={{ fontFamily: 'var(--font-display)', fontWeight: 700, letterSpacing: '-0.01em' }}
      >
        {title}
      </h3>
      <p className="text-sm" style={{ color: 'var(--text-secondary)', lineHeight: 1.55 }}>
        {desc}
      </p>
      {!available && (
        <span
          className="absolute right-4 top-4 rounded-[var(--radius-sm)] px-2 py-0.5 text-[10px] uppercase tracking-wider"
          style={{
            background: 'var(--surface-2)',
            color: 'var(--text-muted)',
            border: '1px solid var(--border-default)',
          }}
        >
          Próximamente
        </span>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Pricing teaser
// ---------------------------------------------------------------------------

/**
 * Compact pricing row above the footer — surfaces the launch prices so
 * curious visitors don't have to navigate to /planes just to see them.
 */
function PricingTeaser() {
  return (
    <section className="border-t" style={{ borderColor: 'var(--border-subtle)', background: 'var(--surface-1)' }}>
      <div className="mx-auto max-w-7xl px-6 py-16">
        <div className="mb-8 flex flex-wrap items-end justify-between gap-3">
          <div>
            <p className="mb-1 text-[11px] uppercase tracking-[0.18em]" style={{ color: 'var(--text-muted)' }}>
              Precios de lanzamiento
            </p>
            <h2 className="text-2xl md:text-3xl" style={{ fontFamily: 'var(--font-display)', fontWeight: 700 }}>
              Paga lo que uses, o vuela sin límite.
            </h2>
          </div>
          <Link to="/planes" className="no-underline">
            <button
              type="button"
              className="inline-flex h-10 items-center gap-1.5 rounded-[var(--radius)] px-4 text-sm font-medium"
              style={{
                background: 'var(--accent)',
                color: 'var(--surface-base)',
                boxShadow: '0 6px 18px rgba(245,158,11,0.32)',
              }}
            >
              Ver todos los planes
              <ArrowRight size={14} strokeWidth={2.4} />
            </button>
          </Link>
        </div>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <TeaserCard
            tag="Prueba"
            title="15 fierros gratis"
            price="Gratis"
            sub="al registrarte + 15 cada mes"
            icon={<FierroIcon size={18} />}
          />
          <TeaserCard
            tag="Pack único"
            title="50 fierros"
            price="S/. 14.90"
            sub="≈ 25 PDFs exportados"
            icon={<FierroIcon size={18} />}
          />
          <TeaserCard
            tag="Suscripción"
            title="Ilimitado mensual"
            price="S/. 39"
            sub="exportaciones sin tope / 30 días"
            featured
            icon={<InfinityIcon size={18} strokeWidth={2} />}
          />
          <TeaserCard
            tag="Suscripción"
            title="Ilimitado anual"
            price="S/. 390"
            sub="2 meses de regalo"
            icon={<InfinityIcon size={18} strokeWidth={2} />}
          />
        </div>
      </div>
    </section>
  );
}

function TeaserCard({
  tag,
  title,
  price,
  sub,
  icon,
  featured,
}: {
  tag: string;
  title: string;
  price: string;
  sub: string;
  icon: React.ReactNode;
  featured?: boolean;
}) {
  return (
    <div
      className="relative rounded-[var(--radius-md)] border p-5"
      style={{
        background: featured ? 'rgba(245,158,11,0.05)' : 'var(--surface-base)',
        borderColor: featured ? 'var(--accent)' : 'var(--border-default)',
        boxShadow: featured ? '0 0 0 1px var(--accent)' : 'none',
      }}
    >
      {featured && (
        <span
          className="absolute -top-2 right-4 rounded-[var(--radius-pill)] px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider"
          style={{ background: 'var(--accent)', color: 'var(--surface-base)' }}
        >
          Más elegido
        </span>
      )}
      <div className="mb-3 flex items-center gap-2">
        <span
          className="flex h-8 w-8 items-center justify-center rounded-[var(--radius-sm)]"
          style={{ background: 'var(--accent)', color: 'var(--surface-base)' }}
        >
          {icon}
        </span>
        <span className="text-[10px] uppercase tracking-[0.14em]" style={{ color: 'var(--text-muted)' }}>
          {tag}
        </span>
      </div>
      <div className="text-sm" style={{ color: 'var(--text-secondary)' }}>
        {title}
      </div>
      <div className="mt-1 font-mono tabular text-2xl" style={{ color: 'var(--text-primary)' }}>
        {price}
      </div>
      <div className="mt-1 text-[12px]" style={{ color: 'var(--text-muted)' }}>
        {sub}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Norms + Footer
// ---------------------------------------------------------------------------

function NormsStrip() {
  return (
    <section
      id="normas"
      className="border-t"
      style={{ borderColor: 'var(--border-subtle)', background: 'var(--surface-base)' }}
    >
      <div className="mx-auto max-w-7xl px-6 py-12">
        <p className="mb-6 text-xs uppercase tracking-wider" style={{ color: 'var(--text-muted)' }}>
          Construido sobre normativa de referencia
        </p>
        <div className="grid grid-cols-2 gap-4 text-sm md:grid-cols-4" style={{ color: 'var(--text-secondary)' }}>
          {[
            ['NTE E.060', 'Concreto Armado'],
            ['NTE E.030', 'Diseño sismorresistente'],
            ['NTE E.070', 'Albañilería'],
            ['NTE E.020', 'Cargas'],
            ['ACI 318-19', 'Building Code Concrete'],
            ['ASTM A615', 'Acero de refuerzo'],
            ['Harmsen', 'PUCP - Concreto Armado'],
            ['Blanco Blasco', 'Estructuración sismorresistente'],
          ].map(([code, label]) => (
            <div key={code} className="flex flex-col">
              <span className="font-mono tabular text-[var(--text-primary)]">{code}</span>
              <span className="text-xs" style={{ color: 'var(--text-muted)' }}>
                {label}
              </span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/**
 * Inline FAQ section on the landing page. Reuses the same accordion
 * component as `/faq` so we don't duplicate the markup; the question list
 * lives in `FAQ_ITEMS`. We surface the first 6 for brevity and link out
 * to the full page for the rest.
 */
function FaqSection() {
  const first = FAQ_ITEMS.slice(0, 6);
  return (
    <section id="faq" className="border-t" style={{ borderColor: 'var(--border-subtle)' }}>
      <div className="mx-auto max-w-4xl px-6 py-20">
        <div className="mb-8 text-center">
          <p className="mb-1 text-[11px] uppercase tracking-[0.18em]" style={{ color: 'var(--text-muted)' }}>
            Soporte
          </p>
          <h2 className="text-3xl" style={{ fontFamily: 'var(--font-display)', fontWeight: 700, letterSpacing: '-0.02em' }}>
            Preguntas frecuentes
          </h2>
          <p className="mt-2 text-sm" style={{ color: 'var(--text-secondary)' }}>
            Lo que la gente nos pregunta antes de suscribirse.
          </p>
        </div>
        <FaqList items={first} />
        <div className="mt-6 text-center">
          <RouterLink to="/faq" className="text-sm no-underline hover:underline" style={{ color: 'var(--accent)' }}>
            Ver todas las preguntas →
          </RouterLink>
        </div>
      </div>
    </section>
  );
}
